/* Enrichment ROI card — admin-only. Aggregated hit rates, credit spend,
 * engine + match-signal breakdown from /api/admin/enrichment-stats. */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

const SIGNAL_LABELS = {
  lastname_in_handle: "Last name in handle",
  fullname_in_handle: "Full name in handle",
  fullname_in_title: "Full name in title",
  site_query_plus_signal: "Targeted site: query",
  legacy_match: "Legacy search match",
  personal_website: "Personal website",
};

export default function EnrichmentRoiCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enrichment-stats");
      setStats(data);
    } catch (e) {
      toast.error("Failed to load enrichment stats", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const t = stats?.totals;

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="enrichment-roi-card">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-slate-400" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Enrichment ROI</h2>
        <span className="text-xs text-slate-500">
          hit rates &amp; credit efficiency across all enrichment runs
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={load}
          disabled={loading}
          data-testid="roi-refresh-button"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {!stats ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {/* Headline metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <Metric label="Enrichment runs" value={t.runs} sub={t.legacy_runs ? `${t.legacy_runs} pre-tracking` : null} testid="roi-runs" />
            <Metric label="Credits spent" value={t.credits} testid="roi-credits" />
            <Metric label="Fields found" value={t.fields_filled} testid="roi-fills" />
            <Metric label="Pending review" value={t.pending} testid="roi-pending" />
            <Metric
              label="Wrong links"
              value={t.wrong ?? 0}
              sub="found later to be someone else's"
              tone="rose"
              testid="roi-wrong"
            />
            <Metric
              label="Repaired links"
              value={t.repaired ?? 0}
              sub="post/video → profile"
              tone="sky"
              testid="roi-repaired"
            />
            <Metric
              label="Credits / fill"
              value={t.credits_per_fill != null ? t.credits_per_fill : "—"}
              testid="roi-credits-per-fill"
            />
            <Metric
              label="Hit rate · net"
              value={t.hit_rate != null ? `${t.hit_rate}%` : "—"}
              sub={t.net_hit_rate != null ? `${t.net_hit_rate}% after wrong links` : null}
              testid="roi-hit-rate"
            />
          </div>

          {/* Per-platform table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="roi-platform-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-2 py-2">Platform</th>
                  <th className="px-2 py-2 text-right">Searched</th>
                  <th className="px-2 py-2 text-right">Found</th>
                  <th className="px-2 py-2 text-right">Pending</th>
                  <th className="px-2 py-2 text-right">Missed</th>
                  <th className="px-2 py-2 text-right" title="Links later judged wrong (Wrong profile / bulk clear)">Wrong</th>
                  <th className="px-2 py-2 text-right" title="Post/video links rewritten to the profile URL">Repaired</th>
                  <th className="px-2 py-2 w-36">Hit rate</th>
                  <th className="px-2 py-2 text-right" title="Hit rate after subtracting wrong links">Net</th>
                  <th className="px-2 py-2 text-right">DB coverage</th>
                </tr>
              </thead>
              <tbody>
                {(stats.by_platform || []).map((p, i) => (
                  <React.Fragment key={p.field}>
                    {p.field === "email" ? (
                      <tr>
                        <td colSpan={10} className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 border-t border-slate-100">
                          Personal-website / scrape fields (not covered by social search)
                        </td>
                      </tr>
                    ) : null}
                    <tr
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50"
                      data-testid={`roi-platform-${p.field}`}
                    >
                    <td className="px-2 py-2 font-medium text-slate-900">{p.label}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.searched}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-emerald-700">{p.filled}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-amber-700">{p.pending}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{p.missed}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${p.wrong ? "text-rose-700 font-medium" : "text-slate-400"}`} data-testid={`roi-wrong-${p.field}`}>{p.wrong ?? 0}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${p.repaired ? "text-sky-700" : "text-slate-400"}`} data-testid={`roi-repaired-${p.field}`}>{p.repaired ?? 0}</td>
                    <td className="px-2 py-2">
                      {p.hit_rate != null ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                p.hit_rate >= 40 ? "bg-emerald-500" : p.hit_rate >= 15 ? "bg-amber-400" : "bg-red-400"
                              }`}
                              style={{ width: `${Math.min(100, p.hit_rate)}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-slate-600 w-11 text-right">{p.hit_rate}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">no data</span>
                      )}
                    </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                        {p.coverage}
                        <span className="text-xs text-slate-400"> ({p.coverage_pct}%)</span>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Engine breakdown */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                By engine
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="roi-engine-breakdown">
                {(stats.by_engine || []).length === 0 ? (
                  <span className="text-xs text-slate-400">No runs yet</span>
                ) : (
                  stats.by_engine.map((e) => (
                    <span
                      key={e.engine}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs"
                    >
                      <span className="font-semibold text-slate-800">{e.engine}</span>
                      <span className="text-slate-500">
                        {e.runs} runs · {e.credits} cr · {e.fills} fills
                      </span>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Match-signal breakdown */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                Winning match signals
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="roi-signal-breakdown">
                {(stats.by_signal || []).length === 0 ? (
                  <span className="text-xs text-slate-400">
                    Signal attribution starts with your next enrichment run
                  </span>
                ) : (
                  stats.by_signal.map((s) => (
                    <span
                      key={s.signal}
                      className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs"
                    >
                      <span className="font-medium text-emerald-900">
                        {SIGNAL_LABELS[s.signal] || s.signal}
                      </span>
                      <span className="text-emerald-700 tabular-nums">{s.fills}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Personal website pass */}
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="roi-personal-website"
          >
            <span className="flex items-center gap-1.5 font-medium text-slate-800">
              <Globe className="h-3.5 w-3.5" /> Personal-website pass
            </span>
            <span>{stats.personal_website.runs} scrapes</span>
            <span>{stats.personal_website.credits} credits</span>
            <span>{stats.personal_website.fields_filled} fields filled</span>
            <span className="text-slate-400">(5 credits per site — fills email, socials, photo, bio)</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, sub, testid }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-lg font-bold text-slate-900 tabular-nums">{value}</div>
      {sub ? <div className="text-[10px] text-slate-400">{sub}</div> : null}
    </div>
  );
}
